import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl: string = environment.apiUrl;

  private jsonHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  constructor(private http: HttpClient) {}

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';
    const body = error.error;

    if (body instanceof ErrorEvent) {
      // Client-side error
      errorMessage = body.message;
    } else if (body && typeof body === 'object' && body.message) {
      // Backend returned a message (ApiResponse.message)
      errorMessage = body.message;
    } else if (body && typeof body === 'object' && body.title) {
      // .NET ProblemDetails
      errorMessage = body.title;
    } else if (body && typeof body === 'object' && body.errors) {
      // .NET validation errors
      errorMessage = ApiService.extractValidationErrors(body.errors) || errorMessage;
    } else if (typeof body === 'string' && body.length > 0 && body.length < 500) {
      // Plain text error body
      errorMessage = body;
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to server';
    } else if (error.status === 401) {
      errorMessage = 'Session expired. Please login again.';
    } else if (error.status === 403) {
      errorMessage = 'Access denied';
    }
    // For 404, 500+ without a backend message, use generic fallback
    // but we already checked for backend message above

    return throwError(() => ({
      status: error.status,
      message: errorMessage,
      error: error.error
    }));
  }

  private static extractValidationErrors(errors: any): string | undefined {
    if (Array.isArray(errors)) return errors.join('. ');
    if (typeof errors === 'object') {
      const msgs: string[] = [];
      for (const key of Object.keys(errors)) {
        const val = errors[key];
        if (Array.isArray(val)) msgs.push(...val);
        else if (typeof val === 'string') msgs.push(val);
      }
      if (msgs.length) return msgs.join('. ');
    }
    return undefined;
  }

  get<T>(endpoint: string, params?: HttpParams): Observable<T> {
    return this.http
      .get<T>(`${this.baseUrl}/${endpoint}`, { params })
      .pipe(catchError(this.handleError));
  }

  post<T>(endpoint: string, data: object = {}): Observable<T> {
    return this.http
      .post<T>(
        `${this.baseUrl}/${endpoint}`,
        JSON.stringify(data),
        { headers: this.jsonHeaders }
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * POST with multipart/form-data (for file uploads)
   */
  postFormData<T>(endpoint: string, formData: FormData): Observable<T> {
    return this.http
      .post<T>(`${this.baseUrl}/${endpoint}`, formData)
      .pipe(catchError(this.handleError));
  }

  put<T>(endpoint: string, data: any): Observable<T> {
    return this.http
      .put<T>(
        `${this.baseUrl}/${endpoint}`,
        JSON.stringify(data),
        { headers: this.jsonHeaders }
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * PUT with multipart/form-data (for file uploads)
   */
  putFormData<T>(endpoint: string, formData: FormData): Observable<T> {
    return this.http
      .put<T>(`${this.baseUrl}/${endpoint}`, formData)
      .pipe(catchError(this.handleError));
  }

  patch<T>(endpoint: string, data: any): Observable<T> {
    return this.http
      .patch<T>(
        `${this.baseUrl}/${endpoint}`,
        JSON.stringify(data),
        { headers: this.jsonHeaders }
      )
      .pipe(catchError(this.handleError));
  }

  delete<T>(endpoint: string): Observable<T> {
    return this.http
      .delete<T>(`${this.baseUrl}/${endpoint}`)
      .pipe(catchError(this.handleError));
  }
}
